import { Button, Space, Spin } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactToPrint from 'react-to-print';
import moment from 'moment';
import { actionPermissions } from '../constants/actionPermissions';
import { DATE_TIME_FORMAT } from '../constants/systemConstants';
import { PrintType, PrintTypeMode, SendMailConfigData } from '../types';
import { alertMessage } from '../utils/alertMessage';
import { hasPermission } from '../utils/hasPermission';
import { ComponentToPrint } from '../views/order/ComponentToPrint';
import SendEmailModal from './plugins/SendEmailModal';
import { getDataWithAuthToken } from '../utils/axiosRequest';
import { MAIL_TEMP_CODE } from '../constants/generalConstants';
import getDashboardStyle from '../utils/getDashboardStyle';

type GeneralPrintContentProps<T extends PrintType> = {
  mode: PrintTypeMode;
  printoutTableData: T;
  onCancel: Function;
  actionPermission?: string;
  isLandscape?: boolean;
  isZoom?: boolean;
  isFullVersion?: boolean;
  beforePrint?: {
    function: Function;
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
  };
};

const GeneralPrintContent = <T extends PrintType>({
  mode,
  printoutTableData,
  onCancel,
  actionPermission = actionPermissions.orderGroup.orderPrint,
  isLandscape = false,
  isZoom = false,
  isFullVersion = true,
  beforePrint,
}: GeneralPrintContentProps<T>) => {
  //General Components
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const ref = useRef<any>();
  const isSubscribed = useRef(true);
  // For Statement
  const [date, setDate] = useState<string>();
  // 打印时间：点击打印时刷新，随页脚一起打印
  const [printTime, setPrintTime] = useState<string>(
    moment().format(DATE_TIME_FORMAT)
  );
  // For sending email
  const [sendMailLoading, setSendMailLoading] = useState<boolean>(false);
  const [isEmailModalVisible, setIsEmailModalVisible] =
    useState<boolean>(false);
  const [emailConfig, setEmailConfig] = useState<SendMailConfigData>();
  const [emailHTML, setEmailHTML] = useState<HTMLElement>();

  // Sets isSubscribed to false if component becomes unmounted
  useEffect(() => {
    isSubscribed.current = false;
  }, []);

  const disableButton = () => {
    const disable = actionPermission ? !hasPermission(actionPermission) : false;
    if (printoutTableData.statementData) {
      return disable || date === undefined;
    }
    return disable;
  };

  // 获取是否是发票模式
  const invoiceMode =
    mode === 'invoice' || mode === 'orderClaim' ||
    (mode === 'order' && getDashboardStyle().isWholeSaleAppType);

  const fetchEmailConfig = async () => {
    try {
      setSendMailLoading(true);
      const response = await getDataWithAuthToken('send/mail/config', {
        params:
          mode === 'statement'
            ? {
                mailTempCode: MAIL_TEMP_CODE.ORDER_STATEMENT,
                // 用户统计和订单列表
                userId: printoutTableData.userId
                  ? printoutTableData.userId
                  : printoutTableData.statementData?.userId,
                statementDate: date,
              }
            : mode === 'orderClaim'
            ? {
                mailTempCode: MAIL_TEMP_CODE.ORDER_CLAIM,
                userId: printoutTableData.userId,
                claimIds: printoutTableData.claimIds?.toString(),
            }
            : invoiceMode
            ? {
                mailTempCode: MAIL_TEMP_CODE.ORDER_INVOICE,
                userId: printoutTableData.userId,
                orderIds: printoutTableData.orderIds?.toString(),
              }
            : {},
      });
      setSendMailLoading(false);
      if (response && response.goodStatus) {
        setEmailConfig(response.data);
      } else {
        alertMessage(
          'error',
          response?.msg || t('general.noResponse'),
          response?.data || undefined
        );
      }
    } catch (error) {
      setSendMailLoading(false);
      console.log(error);
    }
  };

  // 打印/生成邮件前更新打印时间，等state刷新后再获取打印内容
  const refreshPrintTime = () =>
    new Promise<void>((resolve) => {
      setPrintTime(moment().format(DATE_TIME_FORMAT));
      setTimeout(resolve, 0);
    });

  // 不显示打印时间/页码的模式
  const noFooterModes: PrintTypeMode[] = ['transit', 'orderGoods', 'printLabel'];

  // 批量打印（多个订单/Claim）：页码是整个任务连续的，对单个订单没有意义，不显示
  const isBatchPrint =
    ((mode === 'order' || mode === 'invoice' || mode === 'packingSlip') &&
      (printoutTableData.ordersData?.length ?? 0) > 1) ||
    (mode === 'orderClaim' &&
      (printoutTableData.orderClaimsData?.length ?? 0) > 1);

  // 页脚文字：打印时间 + 页码（仅单个打印）
  // 页码用 CSS 计数器 counter(page)/counter(pages)，由浏览器按实际纸张页数生成
  const printFooter = () => {
    if (noFooterModes.includes(mode)) return undefined;
    const timeText = `${t('order.orderDetail.printTime')}: ${printTime}`;
    if (isBatchPrint) return timeText;
    const pageText = t('general.printPageOf', {
      page: '" counter(page) "',
      pages: '" counter(pages) "',
      interpolation: { escapeValue: false },
    });
    return `${timeText}    ${pageText}`;
  };

  const reactPrint = () => {
    return (
      <ReactToPrint
        key={'reactToPrint'}
        pageStyle={pageStyle(isLandscape, printFooter())}
        documentTitle={mode}
        content={() => ref.current}
        onBeforeGetContent={refreshPrintTime}
        onBeforePrint={() =>
          beforePrint
            ? beforePrint.function()
            : alertMessage('info', t('order.alerts.printing'))
        }
        onPrintError={() =>
          alertMessage('error', t('order.alerts.printFailed'))
        }
        trigger={() => (
          <Button disabled={disableButton()} type="primary" key={'print'}>
            {t('actionsColumn.print')}
          </Button>
        )}
      ></ReactToPrint>
    );
  };

  // 发送邮件(生成HTML): Statement/Invoice
  const sendEmailComponent = () => {
    const sendEmailIsNeeded =
      mode === 'statement' || (invoiceMode && printoutTableData.userId); // 打印invoice的必须要有userId
    return sendEmailIsNeeded ? (
      <ReactToPrint
        key={'reactToPrintEmail'}
        pageStyle={pageStyle(isLandscape, printFooter())}
        documentTitle={mode}
        content={() => ref.current}
        onBeforeGetContent={refreshPrintTime}
        trigger={() => (
          <Button disabled={disableButton()} type="primary" key={'sendEmail'}>
            {t('sendEmail.title')}
          </Button>
        )}
        print={async (printIframe: HTMLIFrameElement) => {
          try {
            const iframeDoc = printIframe.contentDocument;

            if (iframeDoc) {
              const head = iframeDoc.head;

              // ✅ 注入当前页面的所有 CSS link，转换为绝对路径
              document
                .querySelectorAll('link[rel="stylesheet"]')
                .forEach((link) => {
                  const href = (link as HTMLLinkElement).href;

                  // 构造绝对路径（即使在 iframe 也能加载）
                  const absoluteHref = new URL(
                    href,
                    window.location.origin
                  ).toString();

                  const newLink = iframeDoc.createElement('link');
                  newLink.rel = 'stylesheet';
                  newLink.href = absoluteHref;
                  head.appendChild(newLink);
                });

              // ✅ 拿到 iframe 的 HTML
              const documentHTML = iframeDoc.documentElement;
              setEmailHTML(documentHTML);

              fetchEmailConfig();
              setIsEmailModalVisible(true);
            } else {
              alertMessage('error', t('sendEmail.alerts.generatePdfFail'));
            }
          } catch (error: any) {
            alertMessage(
              'error',
              `${t('sendEmail.alerts.generatePdfFail')}: ${error.message}`
            );
          }
        }}
      ></ReactToPrint>
    ) : (
      <></>
    );
  };

  const orderClaimComponent = () => {
    // Batch Print
    if (printoutTableData.orderClaimsData) {
      return printoutTableData.orderClaimsData.map((claim, index) => {
        return pageBreakContent(
          index,
          <ComponentToPrint orderClaimData={claim} />
        );
      });
    }
    // Single
    else if (printoutTableData.orderClaimData) {
      return (
        <ComponentToPrint orderClaimData={printoutTableData.orderClaimData} />
      );
    }
  };

  const componentPrint = () => {
    switch (mode) {
      case 'order':
        // Batch Print Orders
        if (printoutTableData.ordersData) {
          return printoutTableData.ordersData.map((order, index) => {
            return pageBreakContent(
              index,
              <ComponentToPrint
                orderData={order}
                footerData={order.footer}
                isFullVersion={isFullVersion}
              />
            );
          });
        }
        break;
      case 'invoice':
        if (printoutTableData.ordersData) {
          return printoutTableData.ordersData.map((order, index) => {
            return pageBreakContent(
              index,
              <ComponentToPrint invoiceData={order} />
            );
          });
        }
        break;
      case 'packingSlip':
        if (printoutTableData.ordersData) {
          return printoutTableData.ordersData.map((order, index) => {
            return pageBreakContent(
              index,
              <ComponentToPrint packingSlipData={order} />
            );
          });
        }
        break;
      case 'driver':
        if (printoutTableData.driverData) {
          return <ComponentToPrint driverData={printoutTableData.driverData} />;
        }
        break;
      case 'transit':
        if (printoutTableData.transitData) {
          return (
            <ComponentToPrint transitData={printoutTableData.transitData} />
          );
        }
        break;
      case 'statement':
        if (printoutTableData.statementData) {
          return (
            <>
              <ComponentToPrint
                statementData={printoutTableData.statementData}
                setDate={setDate}
              />
              {orderClaimComponent()}
            </>
          );
        }
        break;
      case 'orderClaim':
        return orderClaimComponent();
      case 'printLabel':
        if (printoutTableData.printLabel) {
          return <ComponentToPrint printLabel={printoutTableData.printLabel} />;
        }
        break;
      case 'orderGoods':
        if (printoutTableData.orderGoodsData) {
          return (
            <ComponentToPrint
              orderGoodsData={printoutTableData.orderGoodsData}
            />
          );
        }
        break;
      case 'excel':
        if (printoutTableData.excelData) {
          return <ComponentToPrint excelData={printoutTableData.excelData} />;
        }
        break;
    }

    return <></>;
  };

  const renderContent = () => {
    return (
      <>
        <div
          style={{
            width: '100%',
            flexDirection: 'row',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Space>
            {sendEmailComponent()}
            {reactPrint()}
          </Space>
        </div>
        <div
          style={{
            zoom: isZoom ? (screens.sm ? 0.8 : 0.6) : 1,
            padding: 0,
            width: '100%',
          }}
          ref={ref}
        >
          <div style={{ margin: 0 }}>{componentPrint()}</div>
        </div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row-reverse',
          }}
        >
          <Space>
            {reactPrint()}
            <Button key={'cancel'} onClick={onCancel()} type="link">
              {t('general.cancel')}
            </Button>
          </Space>
        </div>

        {emailHTML && (
          <SendEmailModal
            visible={isEmailModalVisible}
            setVisible={setIsEmailModalVisible}
            emailConfig={emailConfig}
            emailHTML={emailHTML}
            orderIds={printoutTableData.orderIds}
          />
        )}
      </>
    );
  };

  return (
    <Spin spinning={beforePrint?.isLoading || sendMailLoading}>
      {renderContent()}
    </Spin>
  );
};

export default GeneralPrintContent;

// 页面样式：footer 放在纸张下边距（@page margin box）里，每页都有且不占用内容区域
// 需要 Chrome/Edge 131+，旧浏览器会忽略 @bottom-center，不影响打印内容
const pageStyle = (isLandscape: boolean, footer?: string) => {
  const size = isLandscape ? 'landscape' : 'portrait';
  if (!footer) {
    return `@page { size: ${size}; margin: 5mm;}`;
  }
  return `@page {
    size: ${size};
    margin: 5mm 5mm 10mm 5mm;
    @bottom-center {
      content: "${footer}";
      font-size: 9px;
      color: #888;
      white-space: pre;
      vertical-align: top;
      padding-top: 2mm;
    }
  }`;
};

// 批量打印，分页处理
const pageBreakContent = (index: number, content: JSX.Element) => {
  return (
    <div style={{ margin: 0 }} key={index}>
      <div className="page-break" />
      {content}
    </div>
  );
};
